import type { ModelKeywordOverlapsResult } from '@app/api/Robots';
import { KEYWORD_OVERLAPS_SHOWN_COUNT } from '@app/components/Modals/BotDetailsModal/constants/keywordOverlaps';
import type {
  BotDetailsFormInterface,
  KeywordOverlapsView,
  PublicationSelectionTypeParameters,
} from '@app/components/Modals/BotDetailsModal/types';
import {
  getInstagramPublicationsIntegrationId,
  getPublicationSelectionTypeParameters,
} from '@app/components/Modals/BotDetailsModal/utils/instagramPublications';
import { getFilledKeywordGroups } from '@app/components/Modals/BotDetailsModal/utils/keywordGroups';
import {
  DEFAULT_CHANNEL_INDEX,
  DEFAULT_SOCIAL_INTEGRATION_ID_INDEX,
} from '@app/constants/defaultChannelIndex';

// Значения формы. Проверка пересечений читает их на каждой перерисовке
// через Form.useWatch, поэтому любое поле может быть undefined, а строка
// условий — пустой или undefined.
export type KeywordMessageConditionValues = Pick<
  BotDetailsFormInterface,
  | 'socialIntegrationType'
  | 'socialIntegrationId'
  | 'publicationSelectionMode'
  | 'instagramPublicationIds'
> & {
  keywordGroups?: ReadonlyArray<string | null | undefined> | null;
};

export interface KeywordMessageConditionParameters
  extends PublicationSelectionTypeParameters {
  KeywordGroups: string[];
  SocialIntegrationType: string | null;
  SocialIntegrationId: number | string | null;
}

// Условия срабатывания робота «Сообщение по ключевым словам» в форме
// TypeParameters. Одна функция для сохранения робота и для проверки
// пересечений: бэкенд сравнивает ровно то, что потом сохранится.
// Пустые строки ключевых слов отбрасываются.
export const getKeywordMessageConditionParameters = ({
  keywordGroups,
  socialIntegrationType,
  socialIntegrationId,
  publicationSelectionMode,
  instagramPublicationIds,
}: KeywordMessageConditionValues): KeywordMessageConditionParameters => {
  const isChannelRestricted = Boolean(
    socialIntegrationType && socialIntegrationType !== DEFAULT_CHANNEL_INDEX
  );

  return {
    KeywordGroups: getFilledKeywordGroups(keywordGroups),
    SocialIntegrationType: isChannelRestricted
      ? socialIntegrationType ?? null
      : null,
    SocialIntegrationId:
      isChannelRestricted &&
      socialIntegrationId !== undefined &&
      socialIntegrationId !== DEFAULT_SOCIAL_INTEGRATION_ID_INDEX
        ? socialIntegrationId
        : null,
    ...getPublicationSelectionTypeParameters({
      publicationSelectionMode,
      instagramPublicationIds,
      isAvailable:
        getInstagramPublicationsIntegrationId(
          socialIntegrationType,
          socialIntegrationId
        ) !== null,
    }),
  };
};

// Ключ условий: по нему ответ проверки сверяется с текущим черновиком.
export const getKeywordConditionsKey = (
  parameters: KeywordMessageConditionParameters
): string => JSON.stringify(parameters);

// Ответ проверки → строки предупреждения. Ответ RTK Query не меняется.
// Показываются первые 5 строк. Остаток считается от TotalCount сервера:
// при IsTruncated в Overlaps пришли не все строки.
export const mapKeywordOverlapsResult = ({
  Overlaps,
  TotalCount,
}: ModelKeywordOverlapsResult): KeywordOverlapsView => {
  const receivedOverlaps = Overlaps ?? [];
  const totalCount = Math.max(TotalCount, receivedOverlaps.length);
  const overlaps = receivedOverlaps
    .slice(0, KEYWORD_OVERLAPS_SHOWN_COUNT)
    .map(
      (
        {
          OtherRobotId,
          OtherRobotName,
          OtherSalesFunnelName,
          OtherSalesFunnelStageName,
          Message,
        },
        index
      ) => ({
        key: `${OtherRobotId}-${index}`,
        message: Message,
        otherRobotName: OtherRobotName,
        otherRobotLocation: `${OtherSalesFunnelName} → ${OtherSalesFunnelStageName}`,
      })
    );

  return {
    overlaps,
    hiddenCount: totalCount - overlaps.length,
  };
};
